import { useCallback, useMemo, useState } from 'react'
import {
  ActionIcon,
  Popover,
  ScrollArea,
  SimpleGrid,
  Tabs,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core'
import { IconMoodSmile } from '@/components/icons'
import {
  REPLY_EMOJI_CATEGORIES,
  REPLY_EMOJI_CATEGORY_ORDER,
} from '@/lib/reply-emoji-categories'
import { readRecentEmojis, saveRecentEmoji } from '@/lib/reply-emoji-recent'

const PICKER_MAX_H = 360
const GRID_COLS = 8

function EmojiCell({ emoji, onPick }: { emoji: string; onPick: (e: string) => void }) {
  return (
    <UnstyledButton
      type="button"
      onClick={() => onPick(emoji)}
      style={{
        fontSize: 22,
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 36,
        borderRadius: 8,
      }}
      className="reply-emoji-cell"
    >
      {emoji}
    </UnstyledButton>
  )
}

type Props = {
  onInsert: (emoji: string) => void
  disabled?: boolean
}

/**
 * Смайлики для поля ответа: те же наборы, что в classic TicketDetail (`emojiCategories` + recent в LS).
 * Popover с вкладками и прокруткой — удобнее, чем фиксированное окно у края экрана.
 *
 * Mantine 8: при controlled `opened`/`onChange` у `Popover.Target` не добавляется onClick —
 * открытие задаём вручную на кнопке.
 */
export function ReplyEmojiPickerButton({ onInsert, disabled }: Props) {
  const [opened, setOpened] = useState(false)
  const [tab, setTab] = useState<string>('recent')
  const [recentVersion, setRecentVersion] = useState(0)

  const recent = useMemo(() => {
    void recentVersion
    return readRecentEmojis()
  }, [recentVersion, opened])

  const pick = useCallback(
    (emoji: string) => {
      saveRecentEmoji(emoji)
      setRecentVersion((v) => v + 1)
      onInsert(emoji)
      setOpened(false)
    },
    [onInsert],
  )

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="top-end"
      shadow="xl"
      radius="md"
      withinPortal
      closeOnClickOutside
      trapFocus
    >
      <Popover.Target>
        <Tooltip label="Смайлики" withArrow position="top" disabled={opened}>
          <ActionIcon
            variant="subtle"
            size="sm"
            radius="md"
            color={opened ? 'brand' : 'gray'}
            aria-label="Смайлики"
            disabled={disabled}
            style={{ pointerEvents: 'auto' }}
            onClick={() => setOpened((o) => !o)}
          >
            <IconMoodSmile size={16} stroke={1.5} />
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown
        p={0}
        style={{
          minWidth: 300,
          maxWidth: 'min(380px, calc(100vw - 24px))',
          overflow: 'hidden',
          border: '1px solid light-dark(rgba(148,163,184,0.28), rgba(255,255,255,0.08))',
          background:
            'radial-gradient(circle at 100% 0, light-dark(rgba(59,130,246,0.08), rgba(59,130,246,0.10)), transparent 38%), light-dark(#ffffff, var(--mantine-color-dark-7))',
        }}
      >
        <Tabs
          value={tab}
          onChange={(v) => setTab(v || 'recent')}
          keepMounted={false}
          styles={{
            list: {
              flexWrap: 'nowrap',
              overflowX: 'auto',
              paddingInline: 6,
              paddingTop: 6,
              background: 'light-dark(rgba(248,250,252,0.82), rgba(26,27,30,0.72))',
              borderBottom: '1px solid light-dark(rgba(148,163,184,0.20), rgba(255,255,255,0.08))',
            },
            tab: {
              padding: '6px 8px',
              fontSize: 18,
              borderRadius: 'var(--mantine-radius-md)',
            },
          }}
        >
          <Tabs.List>
            <Tabs.Tab value="recent" title="Недавние">
              🕐
            </Tabs.Tab>
            {REPLY_EMOJI_CATEGORY_ORDER.map((key) => {
              const c = REPLY_EMOJI_CATEGORIES[key]
              return (
                <Tabs.Tab key={key} value={key} title={c.name}>
                  {c.icon}
                </Tabs.Tab>
              )
            })}
          </Tabs.List>

          <Tabs.Panel value="recent" pt="xs" px="sm" pb="sm">
            <Text size="xs" c="dimmed" fw={500} mb={6}>
              Недавние
            </Text>
            {recent.length === 0 ? (
              <Text size="xs" c="dimmed" py="md" ta="center">
                Выберите смайл на другой вкладке — он появится здесь
              </Text>
            ) : (
              <ScrollArea.Autosize mah={PICKER_MAX_H} type="auto" offsetScrollbars scrollbarSize={9}>
                <SimpleGrid cols={GRID_COLS} spacing={4} verticalSpacing={2}>
                  {recent.map((emoji) => (
                    <EmojiCell key={emoji} emoji={emoji} onPick={pick} />
                  ))}
                </SimpleGrid>
              </ScrollArea.Autosize>
            )}
          </Tabs.Panel>

          {REPLY_EMOJI_CATEGORY_ORDER.map((key) => {
            const c = REPLY_EMOJI_CATEGORIES[key]
            return (
              <Tabs.Panel key={key} value={key} pt="xs" px="sm" pb="sm">
                <Text size="xs" c="dimmed" fw={500} mb={6}>
                  {c.name}
                </Text>
                <ScrollArea.Autosize mah={PICKER_MAX_H} type="auto" offsetScrollbars scrollbarSize={9}>
                  <SimpleGrid cols={GRID_COLS} spacing={4} verticalSpacing={2}>
                    {c.emojis.map((emoji, i) => (
                      <EmojiCell key={`${key}-${i}-${emoji}`} emoji={emoji} onPick={pick} />
                    ))}
                  </SimpleGrid>
                </ScrollArea.Autosize>
              </Tabs.Panel>
            )
          })}
        </Tabs>
      </Popover.Dropdown>
    </Popover>
  )
}
